import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import {
  FilterSlideout,
  createChipSelectSection,
  createSingleSelectSection,
} from '@aimsos/ui';

import {
  CATEGORY_OPTIONS,
  GOVERNED_OPTIONS,
  STATUS_OPTIONS,
  TYPE_OPTIONS,
} from '../constants/widgetLibraryFilters.js';
import { useProfileFilterOptions } from '../hooks/useProfileFilterOptions.js';
import { translateFilterOptions } from '../utils/translateFilterOptions.js';

/** Sentinel shared with the page's `initialState` — deselecting means "no filter". */
const ALL_VALUE = '';

// Prototype parity: Category / Type / Governed render as a single-select
// radio list with an explicit "All ..." row, matching @aimsos/ui's
// `createSingleSelectSection` single-select story — not chips. Status stays
// chip-select, which is how the prototype renders its second dimension too.
const TYPE_MAX_VISIBLE = TYPE_OPTIONS.length + 1;

/**
 * "All filters" panel (CT-5186 / DS-2): the DS `FilterSlideout` driven by the
 * section builders, not a centered modal full of selects.
 *
 * **Staged.** Every section writes to `filtersState.draft`; only `Apply filters`
 * commits it, so dismissing the panel leaves the list untouched. `Clear all`
 * clears the draft — the DS calls each section's `onChange` with an empty value
 * and then this `onClearAll`.
 */
function WidgetLibraryFilterSlideout({ isOpen, onClose, filtersState }) {
  const { t } = useTranslation('dashboards');
  const { draft, setDraftField, applyDraft, clearDraft } = filtersState;
  const profileOptions = useProfileFilterOptions();

  const sections = useMemo(
    () => [
      createSingleSelectSection({
        id: 'category',
        title: t('widgetLibrary.filters.category.label'),
        allValue: ALL_VALUE,
        options: [
          { value: ALL_VALUE, label: t('widgetLibrary.filters.category.all') },
          ...translateFilterOptions(CATEGORY_OPTIONS, t),
        ],
        value: draft.category,
        onChange: (next) => setDraftField('category', next),
      }),
      ...(profileOptions.length
        ? [
          createSingleSelectSection({
            id: 'profile',
            title: t('widgetLibrary.filters.profile.label'),
            allValue: ALL_VALUE,
            options: [
              { value: ALL_VALUE, label: t('widgetLibrary.filters.profile.all') },
              ...profileOptions,
            ],
            value: draft.profile,
            onChange: (next) => setDraftField('profile', next),
          }),
        ]
        : []),
      createChipSelectSection({
        id: 'status',
        title: t('widgetLibrary.filters.status.label'),
        multiple: false,
        options: translateFilterOptions(STATUS_OPTIONS, t),
        value: draft.status,
        onChange: (next) => setDraftField('status', Array.isArray(next) ? (next[0] ?? '') : next),
      }),
      createSingleSelectSection({
        id: 'type',
        title: t('widgetLibrary.filters.type.label'),
        allValue: ALL_VALUE,
        maxVisible: TYPE_MAX_VISIBLE,
        options: [
          { value: ALL_VALUE, label: t('widgetLibrary.filters.type.all') },
          ...translateFilterOptions(TYPE_OPTIONS, t),
        ],
        value: draft.type,
        onChange: (next) => setDraftField('type', next),
      }),
      createSingleSelectSection({
        id: 'governed',
        title: t('widgetLibrary.filters.governed.label'),
        allValue: ALL_VALUE,
        options: [
          { value: ALL_VALUE, label: t('widgetLibrary.filters.governed.all') },
          ...translateFilterOptions(GOVERNED_OPTIONS, t),
        ],
        value: draft.governed,
        onChange: (next) => setDraftField('governed', next),
      }),
    ],
    [
      draft.category, draft.governed, draft.profile, draft.status, draft.type, profileOptions,
      setDraftField, t,
    ],
  );

  // `FilterSlideout` ships English defaults for its own header and footer, so
  // without this the panel's `Clear all` / `Apply filters` stayed in English
  // while every section around them was translated.
  const i18n = useMemo(
    () => ({
      title: t('widgetLibrary.filters.allFilters'),
      close: t('widgetLibrary.filters.close'),
      clearAll: t('widgetLibrary.filters.clearAll'),
      apply: t('widgetLibrary.filters.apply'),
      empty: t('widgetLibrary.filters.noFilters'),
    }),
    [t],
  );

  const handleApply = useCallback(() => {
    applyDraft();
    onClose?.();
  }, [applyDraft, onClose]);

  return (
    <FilterSlideout
      isOpen={isOpen}
      onClose={onClose}
      title={t('widgetLibrary.filters.allFilters')}
      sections={sections}
      onClearAll={clearDraft}
      onApply={handleApply}
      i18n={i18n}
    />
  );
}

WidgetLibraryFilterSlideout.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  filtersState: PropTypes.shape({
    draft: PropTypes.shape({
      category: PropTypes.string,
      status: PropTypes.string,
      type: PropTypes.string,
      governed: PropTypes.string,
      profile: PropTypes.string,
    }).isRequired,
    setDraftField: PropTypes.func.isRequired,
    applyDraft: PropTypes.func.isRequired,
    clearDraft: PropTypes.func.isRequired,
  }).isRequired,
};

export default WidgetLibraryFilterSlideout;
